import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Button, CircularProgress, Alert,
  LinearProgress, Paper, styled
} from '@mui/material';
import { CloudUpload, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const UploadBox = styled(Box)(({ dragging }) => ({
  border: `2px dashed ${dragging ? '#C2823C' : 'rgba(194,130,60,0.35)'}`,
  borderRadius: '20px',
  padding: '48px 32px',
  textAlign: 'center',
  cursor: 'pointer',
  background: dragging ? 'rgba(194,130,60,0.06)' : 'rgba(250,248,244,0.8)',
  transition: 'all 0.25s ease',
  '&:hover': {
    borderColor: '#C2823C',
    background: 'rgba(194,130,60,0.05)',
  },
}));

export default function GuestPhotoUpload() {
  const { token } = useParams();

  const [requestInfo,  setRequestInfo]  = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');
  const [files,        setFiles]        = useState([]);
  const [previews,     setPreviews]     = useState([]);
  const [email,        setEmail]        = useState('');
  const [uploading,    setUploading]    = useState(false);
  const [uploadDone,   setUploadDone]   = useState(false);
  const [dragging,     setDragging]     = useState(false);
  const [progress,     setProgress]     = useState(0);

  useEffect(() => {
    axios.get(`${BASE_URL}/photo-upload/${token}`)
      .then(r => setRequestInfo(r.data))
      .catch(() => setError('This upload link is invalid or has expired.'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleFiles = (newFiles) => {
    const valid = Array.from(newFiles).filter(f => f.type.startsWith('image/'));
    if (!valid.length) return;
    setFiles(prev => [...prev, ...valid]);
    valid.forEach(f => {
      const reader = new FileReader();
      reader.onload = e => setPreviews(prev => [...prev, { src: e.target.result, name: f.name }]);
      reader.readAsDataURL(f);
    });
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleRemove = (idx) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
    setPreviews(prev => prev.filter((_, i) => i !== idx));
  };

  const handleUpload = async () => {
    if (!files.length) return;
    if (!email.trim()) { setError('Please enter your email address.'); return; }
    setUploading(true); setError(''); setProgress(0);

    const formData = new FormData();
    formData.append('email', email);
    files.forEach(f => formData.append('photos', f));

    try {
      await axios.post(`${BASE_URL}/photo-upload/${token}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: e => setProgress(Math.round((e.loaded * 100) / e.total)),
      });
      setUploadDone(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
      <CircularProgress sx={{ color: '#C2823C' }} />
    </Box>
  );

  if (error && !requestInfo) return (
    <Box sx={{ maxWidth: 480, mx: 'auto', mt: 10, p: 4, textAlign: 'center' }}>
      <ErrorIcon sx={{ fontSize: 56, color: '#C4706A', mb: 2 }} />
      <Typography variant="h5" sx={{ fontFamily: "'DM Serif Display', serif", mb: 1 }}>
        Link Invalid
      </Typography>
      <Typography sx={{ color: '#8a7055' }}>{error}</Typography>
    </Box>
  );

  if (uploadDone) return (
    <Box sx={{ maxWidth: 480, mx: 'auto', mt: 10, p: 4, textAlign: 'center' }}>
      <CheckCircle sx={{ fontSize: 72, color: '#6B8C6E', mb: 2 }} />
      <Typography variant="h4" sx={{ fontFamily: "'DM Serif Display', serif", fontStyle: 'italic', mb: 1.5 }}>
        Photos Uploaded!
      </Typography>
      <Typography sx={{ color: '#4a3f30', lineHeight: 1.7 }}>
        Your {files.length} photo{files.length > 1 ? 's have' : ' has'} been sent to the organizer.
        They will review and add them to <strong>{requestInfo?.event_name}</strong>.
      </Typography>
    </Box>
  );

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #FAF8F4 0%, #FFF8F0 100%)',
      py: 6, px: 2,
    }}>
      <Box sx={{ maxWidth: 600, mx: 'auto' }}>

        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Box sx={{
            width: 64, height: 64, borderRadius: '18px', mx: 'auto', mb: 2.5,
            background: 'linear-gradient(135deg, #C2823C, #E8A85A)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 8px 24px rgba(194,130,60,0.35)',
          }}>
            <CloudUpload sx={{ color: '#fff', fontSize: 28 }} />
          </Box>
          <Typography variant="h4" sx={{
            fontFamily: "'DM Serif Display', serif",
            fontStyle: 'italic', fontWeight: 400,
            color: '#1a1510', mb: 1,
          }}>
            Share Your Photos
          </Typography>
          <Typography sx={{ color: '#8a7055', fontSize: '1rem' }}>
            for <strong style={{ color: '#C2823C' }}>{requestInfo?.event_name}</strong>
          </Typography>
        </Box>

        {/* Message from organizer */}
        {requestInfo?.message && (
          <Paper elevation={0} sx={{
            background: '#FFF8F0', border: '1px solid rgba(194,130,60,0.25)',
            borderLeft: '4px solid #C2823C', borderRadius: '12px',
            p: 2.5, mb: 3,
          }}>
            <Typography sx={{ color: '#4a3f30', fontStyle: 'italic', lineHeight: 1.7 }}>
              "{requestInfo.message}"
            </Typography>
          </Paper>
        )}

        {/* Email input */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#8a7055', mb: 1, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            Your Email
          </Typography>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="your@email.com"
            style={{
              width: '100%', padding: '12px 16px',
              border: '1px solid rgba(194,130,60,0.3)',
              borderRadius: '12px', fontSize: '0.95rem',
              fontFamily: "'DM Sans', sans-serif",
              background: '#FFFEFB', color: '#1a1510',
              outline: 'none', boxSizing: 'border-box',
            }}
          />
        </Box>

        {/* Drop zone */}
        <UploadBox
          dragging={dragging ? 1 : 0}
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => document.getElementById('guest-file-input').click()}
        >
          <CloudUpload sx={{ fontSize: 48, color: '#C2823C', mb: 2 }} />
          <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontSize: '1.2rem', color: '#1a1510', mb: 0.5 }}>
            Drop photos here
          </Typography>
          <Typography sx={{ color: '#8a7055', fontSize: '0.88rem' }}>
            or click to browse · JPG, PNG, HEIC supported
          </Typography>
          <input
            id="guest-file-input"
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={e => handleFiles(e.target.files)}
          />
        </UploadBox>

        {/* Previews */}
        {previews.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#8a7055', mb: 1.5, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              {previews.length} photo{previews.length > 1 ? 's' : ''} selected
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 1.5 }}>
              {previews.map((p, i) => (
                <Box key={i} sx={{ position: 'relative', borderRadius: '10px', overflow: 'hidden', aspectRatio: '1', border: '1px solid rgba(194,130,60,0.2)' }}>
                  <img src={p.src} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  <Box
                    onClick={() => handleRemove(i)}
                    sx={{
                      position: 'absolute', top: 4, right: 4,
                      width: 22, height: 22, borderRadius: '50%',
                      background: 'rgba(196,112,106,0.9)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', color: '#fff', fontSize: 14, fontWeight: 700,
                      '&:hover': { background: '#C4706A' },
                    }}
                  >
                    ×
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Upload progress */}
        {uploading && (
          <Box sx={{ mt: 3 }}>
            <LinearProgress variant="determinate" value={progress}
              sx={{ height: 6, borderRadius: 3, bgcolor: 'rgba(194,130,60,0.15)', '& .MuiLinearProgress-bar': { bgcolor: '#C2823C' } }} />
            <Typography sx={{ fontSize: '0.82rem', color: '#8a7055', mt: 1, textAlign: 'center' }}>
              Uploading… {progress}%
            </Typography>
          </Box>
        )}

        {/* Error */}
        {error && <Alert severity="error" sx={{ mt: 2, borderRadius: '10px' }}>{error}</Alert>}

        {/* Submit button */}
        <Button
          fullWidth
          onClick={handleUpload}
          disabled={!files.length || uploading}
          sx={{
            mt: 3, py: 1.8, borderRadius: '50px',
            background: 'linear-gradient(135deg, #C2823C, #E8A85A)',
            color: '#fff', fontFamily: "'DM Sans'", fontWeight: 600,
            fontSize: '1rem', textTransform: 'none',
            boxShadow: '0 6px 20px rgba(194,130,60,0.35)',
            '&:hover': { opacity: 0.92 },
            '&:disabled': { background: 'rgba(194,130,60,0.25)', color: 'rgba(255,255,255,0.6)' },
          }}
        >
          {uploading
            ? <><CircularProgress size={18} sx={{ color: '#fff', mr: 1 }} /> Uploading…</>
            : `Upload ${files.length ? files.length + ' Photo' + (files.length > 1 ? 's' : '') : 'Photos'}`
          }
        </Button>

        <Typography sx={{ textAlign: 'center', color: '#8a7055', fontSize: '0.78rem', mt: 2 }}>
          Link expires {requestInfo?.expires_at?.slice(0, 10)}
        </Typography>
      </Box>
    </Box>
  );
}